// src/pages/Home/HomeComps/Hero.jsx
import { Box, Typography, Button, Stack } from "@mui/material";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { Link } from "react-router-dom";
import crownCursor from "../../../assets/images/crown.png";
import { FloatingPill } from "../../../components/Buttons/ShineButton";
import { TagChip } from "../../../components/shared";
import { useAmbient } from "../../../components/Motion/useAmbient";
import { useReveal } from "../../../components/Motion";

// The portrait is the page's largest paint. It is served from public/ at a fixed
// URL, not imported, so public/index.html can start downloading it while the
// HTML is still parsing, in parallel with the JavaScript. Imported, its hashed
// URL was only known once main.js had run, and on a slow phone the photo
// arrived ~1s after the rest of the hero. Made by scripts/optimize-home-images.mjs.
const heroImg = `${process.env.PUBLIC_URL}/Assets/images/hero-image.webp`;

// Dotted circle component for decoration
const DottedCircle = ({ size, strokeWidth = 2, dashLength = 4, gapLength = 8, top, right, opacity = 0.3 }) => {
  // Fades in last, after the copy has landed.
  const reveal = useReveal({ trigger: "mount", index: 4, variant: "fade" });

  return (
    <Box
      ref={reveal.ref}
      {...reveal.props}
      component="svg"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      sx={{
        position: "absolute",
        top,
        right,
        pointerEvents: "none",
        zIndex: 0,
        display: { xs: "none", sm: "block" },
      }}
    >
      {/* Opacity sits on the circle, not the svg: the reveal animates the svg's
          opacity up to 1, which would override (then snap back to) a faint value. */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={(size / 2) - (strokeWidth / 2)}
        fill="none"
        stroke="rgba(255, 255, 255, 0.5)"
        strokeWidth={strokeWidth}
        strokeDasharray={`${dashLength} ${gapLength}`}
        opacity={opacity}
      />
    </Box>
  );
};

const Hero = () => {
  // The mobile floating pills bob forever; pause them once the hero scrolls away.
  const ambientRef = useAmbient();

  // Above the fold, so these play on mount: badges, headline, subtitle, CTAs in
  // turn. The photo is the likely LCP element, so it does not animate at all: it
  // paints the moment it decodes.
  const badgesReveal = useReveal({ trigger: "mount", index: 0 });
  const headlineReveal = useReveal({ trigger: "mount", index: 1 });
  const subtitleReveal = useReveal({ trigger: "mount", index: 2 });
  const ctaReveal = useReveal({ trigger: "mount", index: 3 });

  return (
    <Box
      ref={ambientRef}
      data-nav-bg="dark"
      sx={{
        width: "100%",
        position: "relative",
        overflow: "hidden",
        minHeight: { xs: "auto", md: "calc(100vh - 80px)" },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        paddingY: { xs: "2rem", md: "3rem" },
        paddingX: { xs: "1.5rem", md: "2rem" },
        paddingBottom: { xs: "3rem", md: "4rem" },
      }}
    >
      {/* Decorative Dotted Circles */}
      <DottedCircle
        size={280}
        strokeWidth={2}
        dashLength={4}
        gapLength={10}
        top={{ xs: "10%", md: "8%" }}
        right={{ xs: "-100px", md: "-40px", lg: "5%" }}
        opacity={0.25}
      />
      <DottedCircle
        size={140}
        strokeWidth={1.5}
        dashLength={3}
        gapLength={6}
        top={{ xs: "50%", md: "45%" }}
        right={{ xs: "5%", md: "8%", lg: "12%" }}
        opacity={0.35}
      />
      {/* Centered Container */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "1100px",
          display: "flex",
          flexDirection: { xs: "column-reverse", md: "row" },
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 3, md: 4 },
          color: "white",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Text Content */}
        <Box
          sx={{
            flex: 1,
            textAlign: { xs: "center", md: "right" },
            maxWidth: { md: "500px" },
          }}
        >
          {/* Role Badge */}
          <Stack
            ref={badgesReveal.ref}
            {...badgesReveal.props}
            direction="row"
            spacing={1}
            sx={{
              justifyContent: { xs: "center", md: "flex-end" },
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            <TagChip label="Product Design" />
            <TagChip label="UX Writing & Content" />
            <TagChip label="Frontend Engineering" />
          </Stack>

      

          {/* Headline */}
          <Typography
            ref={headlineReveal.ref}
            {...headlineReveal.props}
            component="h1"
            variant="heroTitle"
            sx={{
              mb: 2,
              background:
                "linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.8) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Designing clarity into complex products.
          </Typography>

          {/* Subtitle - states the differentiator once; echoes the "one
              connected decision, not three handoffs" line from About.jsx
              so the voice matches sitewide instead of restating it. */}
          <Typography
            ref={subtitleReveal.ref}
            {...subtitleReveal.props}
            variant="heroSubtitle"
            sx={{
              mb: 3,
              color: "rgba(255, 255, 255, 0.9)",
            }}
          >
            Product Designer and UX Writer who also ships the frontend — so
            design, copy, and code move as one decision, not three handoffs.
          </Typography>

          {/* CTA Buttons - desktop only; mobile uses the floating pills on the image */}
          <Box
            ref={ctaReveal.ref}
            {...ctaReveal.props}
            sx={{
              display: { xs: "none", md: "flex" },
              justifyContent: { xs: "center", md: "flex-end" },
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Link to="/work" style={{ textDecoration: "none" }}>
              <Button
                variant="contained"
                sx={{ fontSize: "14px", px: 3, py: 1 }}
              >
                Explore my work
              </Button>
            </Link>
            <Link to="/ux-writing" style={{ textDecoration: "none" }}>
              <Button
                variant="text"
                sx={{
                  color: "rgba(255, 255, 255, 0.85)",
                  fontSize: "14px",
                  px: 2,
                  py: 1,
                  "&:hover": {
                    color: "#fff",
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                Read my UX writing
              </Button>
            </Link>
          </Box>
        </Box>

        {/* Hero Image */}
        <Box
          sx={{
            flex: 1,
            position: "relative",
            display: "flex",
            justifyContent: "center",
            zIndex: 3,
            // On phones the column is reversed and centred, so without a width of
            // its own this box shrank to fit the photo - and a photo that has not
            // loaded yet has no size to fit. It sat at 0px wide, the <img>'s
            // width/height could reserve nothing, and the whole hero dropped
            // ~360px when the file arrived. 380px is what it shrank to anyway.
            width: { xs: "100%", md: "auto" },
            maxWidth: { xs: "380px", md: "none" },
          }}
        >
          {/* width/height give the photo its aspect ratio before it loads. */}
          <img
            src={heroImg}
            width={552}
            height={585}
            fetchPriority="high"
            alt="Haribabu Manoharan - Product Designer, UX Writer, Content Designer and Frontend Engineer"
            style={{
              width: "100%",
              maxWidth: "380px",
              height: "auto",
              display: "block",
              margin: "0",
              cursor: `url(${crownCursor}), auto`,
            }}
          />

          {/* Floating CTA pills - mobile only, overlapping the photo (legacy concept) */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1.25,
              position: "absolute",
              right: { xs: "4%", sm: "8%" },
              bottom: "6%",
              zIndex: 4,
            }}
          >
            <FloatingPill
              component={Link}
              to="/work"
              startIcon={<BusinessCenterOutlinedIcon />}
            >
              Explore my work
            </FloatingPill>
            <FloatingPill
              component={Link}
              to="/ux-writing"
              startIcon={<EditNoteOutlinedIcon />}
              sx={{ animationDelay: "0.8s", opacity: 0.88 }}
            >
              Read my UX writing
            </FloatingPill>
          </Box>
        </Box>
      </Box>

    </Box>
  );
};

export default Hero;
